import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipForward, RotateCcw, CheckCircle2, ArrowRight } from "lucide-react";

const STEP_MS = 2200;

/** A short, self-playing walkthrough of the PIPELINE steps for first-time
 * visitors — a guided callout highlights one step at a time while the row
 * of cards below tracks progress. Pause/skip/replay controls keep it from
 * being a nuisance on repeat visits. */
export function PipelineIntro({ steps }) {
    const [activeStep, setActiveStep] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!playing || done) return;
        const t = setTimeout(() => {
            if (activeStep >= steps.length - 1) {
                setDone(true);
                setPlaying(false);
            } else {
                setActiveStep((s) => s + 1);
            }
        }, STEP_MS);
        return () => clearTimeout(t);
    }, [activeStep, playing, done, steps.length]);

    const restart = () => { setActiveStep(0); setDone(false); setPlaying(true); };
    const skip = () => { setActiveStep(steps.length - 1); setDone(true); setPlaying(false); };

    const current = steps[activeStep];
    const pct = ((activeStep + 1) / steps.length) * 100;

    return (
        <div data-testid="pipeline-intro" className="space-y-3">
            <AnimatePresence mode="wait">
                <motion.div
                    key={done ? "done" : activeStep}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.35 }}
                    className="flex items-center gap-3 panel-elevated px-4 py-3 border-cyan-500/20"
                >
                    {done ? (
                        <>
                            <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                            <p className="text-sm text-slate-200">
                                That&rsquo;s the full pipeline, end to end — <span className="text-cyan-300">{steps.length} stages</span>, fully offline.
                            </p>
                        </>
                    ) : (
                        <>
                            <current.icon size={18} className="text-cyan-300 shrink-0" />
                            <p className="text-sm text-slate-200">
                                <span className="font-mono text-[11px] text-cyan-400 uppercase tracking-wider mr-2">Step {activeStep + 1}/{steps.length}</span>
                                <span className="text-white font-medium">{current.t}</span> — {current.d}
                            </p>
                        </>
                    )}
                    <div className="ml-auto flex items-center gap-3 shrink-0">
                        {!done ? (
                            <button
                                data-testid="pipeline-intro-playpause-btn"
                                onClick={() => setPlaying((p) => !p)}
                                className="text-slate-500 hover:text-cyan-300 transition-colors"
                                aria-label={playing ? "Pause intro" : "Play intro"}
                            >
                                {playing ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                        ) : (
                            <button
                                data-testid="pipeline-intro-replay-btn"
                                onClick={restart}
                                className="inline-flex items-center gap-1 text-[11px] font-mono text-cyan-400 hover:text-cyan-300 transition-colors"
                            >
                                <RotateCcw size={12} /> Replay
                            </button>
                        )}
                        {!done && (
                            <button
                                data-testid="pipeline-intro-skip-btn"
                                onClick={skip}
                                className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-500 hover:text-slate-300 transition-colors"
                            >
                                Skip <SkipForward size={12} />
                            </button>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>

            <div className="h-[2px] bg-white/5 rounded-full overflow-hidden">
                <motion.div
                    className="h-full bg-gradient-to-r from-cyan-300 to-cyan-600"
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.4 }}
                />
            </div>

            <div className="flex flex-wrap lg:flex-nowrap items-stretch gap-2 overflow-x-auto pb-1">
                {steps.map((p, i) => {
                    const isActive = i === activeStep && !done;
                    const isPast = i < activeStep || done;
                    return (
                        <React.Fragment key={p.t}>
                            <motion.div
                                animate={{ opacity: isActive || isPast ? 1 : 0.35, scale: isActive ? 1.04 : 1 }}
                                transition={{ duration: 0.3 }}
                                className={`panel p-3.5 rounded-[4px] w-[150px] shrink-0 flex flex-col relative ${isActive ? "border-cyan-400/60 shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.25)]" : ""}`}
                            >
                                {isPast && !isActive && (
                                    <CheckCircle2 size={12} className="absolute top-2 right-2 text-emerald-400" />
                                )}
                                <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-cyan-500/20 to-rose-500/20 border border-cyan-500/25 flex items-center justify-center mb-2">
                                    <p.icon size={15} className="text-cyan-300" />
                                </div>
                                <div className="text-xs text-white font-medium leading-snug">{p.t}</div>
                                <div className="text-[10px] text-slate-500 mt-1 leading-relaxed flex-1">{p.d}</div>
                            </motion.div>
                            {i < steps.length - 1 && (
                                <div className="flex items-center justify-center shrink-0 text-slate-600">
                                    <ArrowRight size={16} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}
